import * as React from 'react';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { Camera, DeviceGroupId, DeviceGroupMap, DeviceMap, StreamId, DeviceId } from 'types';
import { State } from 'reducers';
import { isLoadingDevices, getDevices, isCamera } from 'selectors/deviceSelectors';
import { isLoadingDeviceGroups, getDeviceGroups } from 'selectors/deviceGroupSelectors';
import { TreeView } from 'ui/shared/TreeView';
import { waitUntilLoaded } from 'ui/shared/waitUntilLoaded';
import { makeStreamId, splitStreamId, isStreamId } from 'util/streamId';
import { Folder } from 'ui/shapes';

import './CameraTree.css';

type Item = any; // tslint:disable-line:no-any
type Node = {
    children: Node[];
    key: string;
    id: string;
    item: Item;
    checked: boolean;
    selected: boolean;
};

const makeTreeNode = (item: Item, parentId: DeviceGroupId | null, id: string, checked: boolean, selected: boolean, children: Node[] = []): Node => ({
    children,
    key: parentId ? `${parentId}_${id}` : id,
    id,
    item,
    checked,
    selected,
});

const noParent = '00000000-0000-0000-0000-000000000000';

type GroupMembershipMap = {
    [groupId: string]: DeviceId[];
};

const hasAtLeastOneDevice = (node: Node, deviceIds: { [deviceId: string]: boolean }): boolean => {
    return !!deviceIds[node.item.deviceId] || node.children.some(child => hasAtLeastOneDevice(child, deviceIds));
};

const makeTreeData = (
    showStreams: boolean,
    hideEmptyGroups: boolean,
    devices: DeviceMap,
    filteredDeviceIdMap: { [deviceId: string]: boolean },
    deviceIdsByParentId: GroupMembershipMap,
    deviceGroups: DeviceGroupMap,
    checked: { [id: string]: boolean },
    selected: { [id: string]: boolean },
    parentId: string | null = null,
    orphanGroupIds: { [id: string]: boolean } = {},
) => {
    // Add the device groups with the specified parent
    const groupKeys = Object.keys(deviceGroups).filter(id =>
        deviceGroups[id] && // the group exists
        (
            (!parentId && deviceGroups[id].parentGroupId === noParent) || // Root group
            (parentId && deviceGroups[id].parentGroupId === parentId) || // Non-root group
            (!parentId && typeof orphanGroupIds[id] !== 'undefined') // Orphan group (i.e. non-visible parent)
        ) &&
        !deviceGroups[id].isThinClientsGroup // AIR-34997: Exclude Thin Clients group for now
    );
    const groupNodes = groupKeys.map(key => {
        const deviceGroup = deviceGroups[key];
        const treeData = makeTreeData(showStreams, hideEmptyGroups, devices, filteredDeviceIdMap, deviceIdsByParentId, deviceGroups, checked, selected, deviceGroup.groupId); // tslint:disable-line:max-line-length
        const node = makeTreeNode(deviceGroup, parentId, deviceGroup.groupId, false, false, treeData);
        return node;
    });

    // Add all the unique devices with the specified parent (made unique because can have multiple streams per camera)
    const deviceKeys = parentId && deviceIdsByParentId[parentId] || [];
    const deviceNodes = deviceKeys.map(deviceId => {
        const device = devices[deviceId];
        const effectiveId =
            isCamera(device) && (device as Camera).streams.length === 1 || !showStreams
                ? makeStreamId(deviceId, 0) // Cheat and pass the stream ID for the first (and only) stream
                : deviceId;
        const treeData = makeTreeData(showStreams, hideEmptyGroups, { [deviceId]: device }, filteredDeviceIdMap, {}, {}, checked, selected, deviceId);
        const node = makeTreeNode(device, parentId, effectiveId, checked[effectiveId], selected[effectiveId], treeData);
        return node;
    });

    // Add all the streams with the specified parent
    const camera = parentId && devices[parentId] as Camera;
    const streamNodes = showStreams && camera && isCamera(camera) && camera.streams.length > 1 && camera.streams.map(stream => {
        const streamId = makeStreamId(camera.deviceId, stream.streamIndex);
        const node = makeTreeNode({ ...camera, ...stream }, parentId, streamId, checked[streamId], selected[streamId]);
        return node;
    }) || [];

    return [
        ...streamNodes,
        ...deviceNodes,
        ...(hideEmptyGroups ? groupNodes.filter(node => hasAtLeastOneDevice(node, filteredDeviceIdMap)) : groupNodes),
    ].sort((a, b) => a.item.name.localeCompare(b.item.name));
};

const noop = () => {}; // tslint:disable-line

const styles = {
    icon: {
        fill: '#fff',
        marginRight: '.5em',
        opacity: .75,
    },
    opacity75: {
        opacity: .75,
    },
    opacity90: {
        opacity: .9,
    },
    opacity25: {
        opacity: .25,
    },
    name: {
        marginLeft: '.25em',
    },
    resolution: {
        marginLeft: '1em',
        opacity: .5,
        fontSize: '.7em',
    },
    separator: {
        opacity: .65,
        margin: '0 .2em',
    },
};

type NodeProps = {
    id: string;
    item: Item;
    select?: 'checkbox' | 'radio';
    checked: boolean;
    handleClicked?: React.MouseEventHandler<HTMLElement>;
    handleCheckChanged?: React.ChangeEventHandler<HTMLElement>;
};

const GroupNode = ({ item, select, checked, handleCheckChanged = noop, t }: NodeProps & TranslateProps) =>
    <div style={ styles.opacity90 }>
        { /* TODO: Maybe add this later. Also need to write logic for auto-checking all children and determining parent state based on child state
         select === 'checkbox' &&
            <input
                id={ `camera-tree-${item.id}-checkbox` }
                type="checkbox"
                checked={ checked }
                onChange={ handleCheckChanged }
            />
        */ }
        <Folder width="1em" height="1em" style={ styles.icon }/>
        <span>
            { item.isDevicesGroup ? t('2887') : item.isThinClientsGroup ? t('ThinClientsGroup') : item.name }
        </span>
    </div>
;

const stopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    return true;
};

const CameraNode = ({ id, item, select, checked, handleClicked = noop, handleCheckChanged = noop }: NodeProps) =>
    <div
        onClick={ select ? handleClicked : noop }
        data-nodeid={ id }
    >
        { select &&
            <input
                id={ `camera-tree-${item.deviceIndex}-checkbox` }
                data-nodeid={ id }
                type={ select }
                checked={ checked }
                onChange={ handleCheckChanged }
                onClick={ stopPropagation }
            />
        }
        <span style={ styles.opacity75 }>
            { item.deviceIndex }
        </span>
        <span style={ styles.opacity25 }>
            )
        </span>
        <span style={ styles.name }>
            { item.name }
        </span>
        { item.streams.length === 1 &&
            <span style={ styles.resolution }>
                <span>
                    { item.streams[0].width }
                </span>
                <span style={ styles.separator }>
                    x
                </span>
                <span>
                    { item.streams[0].height }
                </span>
            </span>
        }
    </div>
;

const StreamNode = ({ id, item }: { id: string; item: Item }) =>
    <div
        data-nodeid={ id }
    >
        <span style={ styles.opacity75 }>
            #
        </span>
        <span style={ styles.name }>
            { item.streamIndex + 1 }
        </span>
        <span style={ styles.resolution }>
            <span>
                { item.width }
            </span>
            <span style={ styles.separator }>
                x
            </span>
            <span>
                { item.height }
            </span>
        </span>
    </div>
;

const calculateLevelsToAutoExpand = (deviceCount: number) => {
    if (deviceCount >= 1000) return 1;
    if (deviceCount >= 100) return 2;
    return 3;
};

type Props = OwnProps & StateProps & RecomposeProps & OemProps & TranslateProps;

type CTState = {
    data: Node[];
    checked: {
        [streamId: string]: boolean;
    };
};

class RawCameraTree extends React.Component<Props, CTState> {
    constructor(props: Props) {
        super(props);
        const {
            checkedStreamIds = {},
            selectedStreamIds = {},
        } = props;

        this.state = {
            data: this.makeData(this.props, checkedStreamIds, selectedStreamIds),
            checked: checkedStreamIds,
        };
    }

    makeData = (props: Props, checkedStreamIds: {}, selectedStreamIds: {}) => {
        const { loading, devices, streamIds, deviceGroups, showStreams = false, hideEmptyGroups = false } = props;

        if (loading || !devices || !deviceGroups) {
            return [];
        }

        // From the given list of filtered stream IDs, compute a list of filtered device IDs
        const dupeDeviceIds = streamIds.map(splitStreamId).map(({ deviceId }) => deviceId);
        const uniqueDeviceIdMap = dupeDeviceIds.reduce((unique, deviceId) => { unique[deviceId] = true; return unique; }, {});
        const uniqueDeviceIds = Object.keys(uniqueDeviceIdMap);

        // From the given list of devices and device groups, determine group membership
        const deviceIdsByParentId = Object.keys(deviceGroups).reduce((dict, groupId) => ({ ...dict, [groupId]: [] }), {});
        uniqueDeviceIds.forEach(deviceId => {
            const device = devices[deviceId];
            device.groupIds.forEach(groupId => {
                // Ensure we have a group for these devices (e.g. we currently exclude Thin Clients)
                if (deviceIdsByParentId[groupId]) {
                    deviceIdsByParentId[groupId].push(deviceId);
                }
            });
        });
        
        const filteredDeviceIdMap = uniqueDeviceIds.reduce((o, id) => { o[id] = true; return o; }, {});

        // AIR-38383: Determine which groups don't have a visible path to the tree root ('All Devices').
        //            These will end up being placed adjacent to the root in the UI (so they would appear
        //            as if they were children of 'All Devices')
        const orphanGroupIds =
            Object.keys(deviceGroups)
                .map(groupId => deviceGroups[groupId])
                .filter(group => typeof deviceGroups[group.parentGroupId] === 'undefined')
                .reduce((all, group) => ({
                    ...all,
                    [group.groupId]: true,
                }), {});

        return makeTreeData(showStreams, hideEmptyGroups, devices, filteredDeviceIdMap, deviceIdsByParentId, deviceGroups, checkedStreamIds, selectedStreamIds, null, orphanGroupIds); // tslint:disable-line
    }

    componentDidUpdate(oldProps: Props) {
        let { checked } = this.state;
        
        if (this.props.checkedStreamIds !== oldProps.checkedStreamIds) {
            checked = this.props.checkedStreamIds;
        }

        // Update the tree data if any of the components have changed
        if (this.props.deviceGroups !== oldProps.deviceGroups ||
            this.props.devices !== oldProps.devices ||
            this.props.streamIds !== oldProps.streamIds ||
            this.props.select !== oldProps.select ||
            this.props.checkedStreamIds !== oldProps.checkedStreamIds ||
            this.props.selectedStreamIds !== oldProps.selectedStreamIds ||
            this.props.hideEmptyGroups !== oldProps.hideEmptyGroups
        ) {
            this.setState({
                data: this.makeData(this.props, checked, this.props.selectedStreamIds),
                checked,
            });
        }
    }

    onClick = (id: string) => {
        const { onClickStream } = this.props;
        if (!onClickStream) return;

        // Was a stream clicked?
        if (isStreamId(id)) {
            onClickStream(id as StreamId);
        }
    }

    handleClickCamera = (e: React.MouseEvent<HTMLElement>) => {
        const nodeId = e.currentTarget.dataset.nodeid;
        this.onCheckCamera(nodeId as StreamId);
    }

    handleCheckCamera = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nodeId = e.currentTarget.dataset.nodeid;
        this.onCheckCamera(nodeId as StreamId);
    }

    onCheckCamera = (id: StreamId) => {
        // Note: currently checkboxes are only used when streams are hidden
        // Note: when streams are hidden, camera nodes have stream ids (of the 0th stream)
        const { onCheckChanged, select } = this.props;
        if (!onCheckChanged) return;
        let { checked } = this.state;
        if (select === 'checkbox') {
            checked = {
                ...checked,
                [id]: !checked[id],
            };
        }
        else if (select === 'radio') {
            checked = {
                [id]: true,
            };
        }
        this.setState({ checked }, () => {
            const checkedStreamIds = Object.keys(checked).filter(streamId => !!checked[streamId]) as StreamId[];
            onCheckChanged(checkedStreamIds);
        });
    }

    handleCheckGroup = (e: React.ChangeEvent<HTMLInputElement>) => {
        //const id = e.currentTarget.dataset['dgid'] as DeviceGroupId;
        // Recursively check/uncheck all children
        // TODO
    }

    renderLabel = (node: Node) => {
        if (typeof node.item.streamIndex === 'number') {
            return (
                <StreamNode
                    id={ node.id }
                    item={ node.item }
                />
            );
        }
        else if (typeof node.item.deviceType === 'string') {
            return (
                <CameraNode
                    id={ node.id }
                    item={ node.item }
                    checked={ !!this.state.checked[node.id] }
                    select={ this.props.select }
                    handleClicked={ this.handleClickCamera }
                    handleCheckChanged={ this.handleCheckCamera }
                />
            );
        }
        else {
            return (
                <GroupNode
                    id={ node.id }
                    item={ node.item }
                    checked={ !!this.state.checked[node.id] }
                    select={ this.props.select }
                    handleCheckChanged={ this.handleCheckGroup }
                    t={ this.props.t }
                />
            );
        }
    }

    render() {
        const { data } = this.state;
        const { devices, styles, id, hideRoot } = this.props;
        return (
            <TreeView
                id={ id }
                data={ data }
                onClick={ this.onClick }
                hideRoot={ hideRoot }
                levelsToAutoExpand={ devices ? calculateLevelsToAutoExpand(Object.keys(devices).length) : 0 }
                color={ styles.Window.color as string }
            >
                { this.renderLabel }
            </TreeView>
        );
    }
}

export type OwnProps = {
    id: string;
    streamIds: ReadonlyArray<StreamId>;
    checkedStreamIds?: ReadonlyArray<StreamId>;
    selectedStreamId: StreamId | null; // For now just take one or zero... but the plumbing is there to accept many
    select?: 'checkbox' | 'radio';
    showStreams?: boolean;
    showResolutions?: boolean;
    hideEmptyGroups?: boolean;
    onClickStream?: (streamId: StreamId) => void;
    onCheckChanged?: (checked: ReadonlyArray<StreamId>) => void;
};
type StateProps = {
    loading: boolean;
    devices: DeviceMap | null;
    deviceGroups: DeviceGroupMap | null;
};
type RecomposeProps = {
    checkedStreamIds: { [streamId: string]: boolean }; // Overwrites the property type from OwnProps
    selectedStreamIds: { [streamId: string]: boolean };
    hideRoot: boolean;
};

const mapStateToProps = (state: State) => {
    const devices = getDevices(state);
    const deviceGroups = getDeviceGroups(state);
    const loading = isLoadingDevices(state) || isLoadingDeviceGroups(state);
    return {
        // Only show a loading spinner for the first load
        loading: loading && (!devices || !deviceGroups),
        devices,
        deviceGroups,
    };
};

export const CameraTree = compose<Props, OwnProps>(
    skin,
    translate(),
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    waitUntilLoaded({ size: 'small' }),
    withProps(({ deviceGroups, checkedStreamIds = [], selectedStreamId }) => ({
        // Convert the array into a lookup
        checkedStreamIds: checkedStreamIds.reduce((all, streamId) => ({ ...all, [streamId]: true }), {}),
        selectedStreamIds: selectedStreamId ? { [selectedStreamId]: true } : {},
        hideRoot: Object.keys(deviceGroups || {}).some((groupId: DeviceGroupId) =>
            (deviceGroups || {})[groupId].parentGroupId === '00000000-0000-0000-0000-000000000000'
        ),
    })),
)(RawCameraTree);



// WEBPACK FOOTER //
// ./src/ui/shared/CameraTree.tsx